import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BasicModal, Button, Step, Stepper } from "@life-line-lab/components-react";
import { find, isEmpty, isUndefined, omitBy, isNil } from "lodash";
import { FilePayload, FileTypeFilePayload } from "../../../types/common.types";
import errorHandler from "Utils/error-handler";
import { getBase64 } from "Helpers/index";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import DriverInfoSchema from "./stepperContents/driver-info/driver-info.validation";
import VehicleInfoSchema from "./stepperContents/vehicle-info/vehicle-info.validation";
import PaymentDetailsSchema from "./stepperContents/payment-details/payment-details.validation";
import EmergencyContactsSchema from "./stepperContents/emergency-contacts/emergency-contacts.validation";
import "./driver-create-form.scss";
import {
  DriverCategories,
  DriverCategoriesTabs,
  DriverCreationFormType,
} from "Types/new_driver_types";
import { generateSteps } from "Helpers/generate-stepper-steps";

type CommonDriverProps = {
  onBack?: () => void;
  formType?: DriverCategoriesTabs;
  onSubmitEdit?: (data) => void;
};

interface ExternalDriverCreationProps extends CommonDriverProps {
  onSubmitExternal?: (data) => void;
  onSubmit?: never;
  driverDetails?: any;
  isSubmitLoading: boolean;
}

interface DriverCreationProps extends CommonDriverProps {
  onSubmit?: (data) => void;
  onSubmitExternal?: never;
  driverDetails?: any;
  isSubmitLoading: boolean;
}

export type DriverCreateFormProps = DriverCreationProps | ExternalDriverCreationProps;

const initialSteps: Step[] = generateSteps<Step>(5);
const initialInput: Partial<DriverCreationFormType> = {
  doesRiderOwnVehicle: "false",
  identityType: "NRIC",
  vehicleCategory: "NORMAL",
};
const initialPrevImg = {
  fileName: "",
  fileType: FileTypeFilePayload.IMAGE,
  base64String: "",
};
const initialUploadModal = {
  title: "",
  type: "",
  format: "",
  open: false,
};
const initialError = {};

// validation schema for each stepper components
const validationSchemas = [
  DriverInfoSchema,
  VehicleInfoSchema,
  PaymentDetailsSchema,
  EmergencyContactsSchema,
];

const arr = Array(5).fill(0);

const dataTableDetails = [
  { type: "nric_passport", format: "pdf" },
  { type: "driving_license", format: "pdf" },
  { type: "road_tax", format: "pdf" },
  { type: "photo_of_vehicle", format: "image" },
];

const editFormType: DriverCategories = [
  DriverCategoriesTabs.DRIVER_INFO,
  DriverCategoriesTabs.VEHICLE_INFO,
  DriverCategoriesTabs.PAYMENT_INFO,
  DriverCategoriesTabs.EMERGENCY_INFO,
  DriverCategoriesTabs.ATTACHMENT_INFO,
];

const useDriverCreateForm = () => {
  const { t } = useTranslation();

  const [input, setInput] = useState<Partial<DriverCreationFormType>>(initialInput);
  const [prevImg, setPrevImg] = useState<FilePayload>(initialPrevImg);
  const [error, setError] = useState<any>(initialError);
  const [modal, setModal] = useState<boolean>(false);
  const [uploadModal, setUploadModal] = useState<any>(initialUploadModal);
  const [uploadFileList, setUploadFileList] = useState<any>([]);
  const [isShowAlert, setIsShowAlert] = useState<boolean>(false);
  const [currentFileUploadType, setCurrentFileUploadType] = useState<string>();
  const [avatarImg, setAvatarImg] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [steps, setSteps] = useState<any>(initialSteps);
  let [current, setCurrent] = useState<number>(0);

  /**
   * function to handle change in text input
   * @param e
   */
  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput({ ...input, [e.target.name]: e.target.value });
    setError({ ...error, [e.target.name]: null });
  };

  /**
   * function to handle change in radio input
   * @param e - Radio event
   */
  const handleRadioChange = (e) => {
    setInput({ ...input, [e.target.name]: e.target.value });
    setError({ ...error, [e.target.name]: null });
  };

  /**
   * function to handle change in select input
   * @param {string | string[]} value - selected data
   * @param name - name of select input
   */
  const handleSelectChange = (value: string | string[], name: string) => {
    setError({ ...error, [name]: null });
    setInput({ ...input, [name]: value });
  };

  /**
   * function to handle change in checkbox input
   * @param e - checkbox event
   */
  const handleCheckboxChange = (e: CheckboxChangeEvent) => {
    setError({ ...error, acceptTerms: null });
    setInput({ ...input, acceptTerms: e.target.checked });
  };

  /**
   * function to handle change in phone input
   * @param value - phone data object
   * @param name - name of input
   */
  const handlePhoneChange = (value: { dialCode: string; number: string }, name: string) => {
    setError(initialError);
    setInput({ ...input, [name]: value });
  };

  const handleAvatarClick = () => {
    setUploadModal((prevUploadModal) => ({
      ...prevUploadModal,
      open: true,
      type: "avatar",
      format: ".png, .jpg, .jpeg",
      title: t("label.uploadProfilePicture"),
    }));
  };

  // function to show alert box on modal
  const handleShowFileCompatibilityAlert = () => {
    setIsShowAlert(true);
  };

  // function to hide alert box on modal
  const handleHideFileCompatibilityAlert = () => {
    setIsShowAlert(false);
  };

  /**
   * function to handle changes in uploaded file
   * @param param0
   * @param type
   * @param format
   */
  const handleFileChange = ({ fileList: fileList }, type, format) => {
    if (isEmpty(fileList)) return;

    setError(initialError);
    const isFileTypeCompatible = handleValidateFileUploadType(fileList, format);

    if (isFileTypeCompatible) {
      const newUploadFileList = uploadFileList.filter((file) => {
        return file.source !== type;
      });
      const fileObj = { ...fileList[0], source: type };
      newUploadFileList.push(fileObj);
      setUploadFileList(newUploadFileList);
      setCurrentFileUploadType(type);
      handleHideFileCompatibilityAlert();
    } else handleShowFileCompatibilityAlert();
  };

  /**
   * function to validate uploaded file type
   * @param fileList - file data
   * @param format - accepted file format/type e.g. jpeg, pdf, etc...
   * @returns {boolean} - true or false
   */
  const handleValidateFileUploadType = (fileList, format) => {
    const regexFormat = format.match(/\w+/g);
    // if format accepts jpeg, jpg, png
    if (regexFormat.length > 1) {
      return (
        fileList[0].type?.includes("png") ||
        fileList[0].type?.includes("jpg") ||
        fileList[0].type?.includes("jpeg")
      );
    } else {
      return fileList[0]?.type?.includes(regexFormat);
    }
  };

  const handleCancelUpload = () => {
    setUploadModal(initialUploadModal);
    const newUploadFileList = uploadFileList.filter((file) => {
      return file.source !== currentFileUploadType;
    });
    setUploadFileList(newUploadFileList);
  };

  const handleShowUploadModal = (recordData) => {
    setUploadModal((prevUploadModal) => ({
      ...prevUploadModal,
      open: true,
      type: recordData.type,
      format: recordData.format === "pdf" ? ".pdf" : ".png, .jpg, .jpeg",
      title: t(`title.${recordData.type}`),
    }));
  };

  // this function is used for the icon in the table (although it's same as the handleFileRemove)
  const handleDeleteFile = (type: string) => {
    const newUploadFileList = uploadFileList.filter((file) => {
      return file.source !== type;
    });
    setUploadFileList(newUploadFileList);
  };

  // this is used within the upload modal (a delete icon will appear after successful upload)
  const handleFileRemove = (fileObj) => {
    const newUploadFileList = uploadFileList.filter((file) => {
      return file.source !== fileObj.source;
    });
    setUploadFileList(newUploadFileList);
  };

  /**
   * function to store uploaded file into input
   * @returns
   */
  const handleConfirmFile = () => {
    handleHideFileCompatibilityAlert();
    if (isNil(uploadFileList)) return;

    //TESTING
    const fileURL = input.file?.file_url || "";
    if (fileURL && input.file) {
      setPrevImg(input.file);
    }

    avatarImg.forEach(async (fileObj: any) => {
      if (fileObj?.fileID == undefined) {
        let base64Data: any = "";
        base64Data = await getBase64(fileObj.originFileObj);
        setInput({
          ...input,
          file: {
            fileName: fileObj.name,
            fileType: FileTypeFilePayload.IMAGE,
            base64String: base64Data,
          },
        });
      }
    });

    uploadFileList.forEach(async (file) => {
      let base64Data: any = "";
      base64Data = await getBase64(file.originFileObj);
      setInput({
        ...input,
        [file.source]: {
          fileName: file.name,
          fileType: file.type.includes("image")
            ? FileTypeFilePayload.IMAGE
            : FileTypeFilePayload.DOCUMENT,
          base64String: base64Data,
        },
      });
    });
    setUploadModal(initialUploadModal);
  };

  /**
   * function to go to previos stepper component form
   */
  const prev = () => {
    const prevStep = find(steps, (_, index) => {
      return index === (current - 1).toString();
    });

    if (prevStep) {
      prevStep!.status = "process";
      steps.splice(current - 1, 1, prevStep);
      setSteps(steps);
    }

    if (current !== 0) {
      current--;
      setCurrent(current);
    }
  };

  /**
   * function to go to next stepper component form
   */
  const next = () => {
    try {
      let validateValue = validationSchemas[current].validateSync(
        omitBy(omitBy(input, isNil), isEmpty),
        {
          strict: true,
          abortEarly: true,
          stripUnknown: false,
        },
      );

      if (!isNil(validateValue)) {
        const currentStep = find(steps, (_, index) => {
          return index === current.toString();
        });

        if (current !== arr.length - 1) {
          if (currentStep) {
            currentStep!.status = "success";
            steps.splice(current, 1, currentStep);
            setSteps(steps);
          }

          current++;
          setCurrent(current);
        }
      }
    } catch (err) {
      let { path, value, length }: any = errorHandler.validation(err);
      console.log(err);

      setError({
        ...error,
        [path]: t(`${value}`, { field: t(`label.${path}`), length: length }),
      });
    }
  };

  return {
    initialUploadModal,
    validationSchemas,
    dataTableDetails,
    editFormType,
    input,
    setInput,
    prevImg,
    setPrevImg,
    error,
    setError,
    modal,
    setModal,
    uploadModal,
    setUploadModal,
    uploadFileList,
    setUploadFileList,
    isShowAlert,
    setIsShowAlert,
    currentFileUploadType,
    setCurrentFileUploadType,
    avatarImg,
    setAvatarImg,
    isLoading,
    setIsLoading,
    steps,
    setSteps,
    current,
    setCurrent,
    handleFieldChange,
    handleRadioChange,
    handleCheckboxChange,
    handlePhoneChange,
    handleSelectChange,
    handleAvatarClick,
    handleShowFileCompatibilityAlert,
    handleHideFileCompatibilityAlert,
    handleFileChange,
    handleCancelUpload,
    handleShowUploadModal,
    handleDeleteFile,
    handleFileRemove,
    handleConfirmFile,
    prev,
    next,
  };
};

export default useDriverCreateForm;
